/**
 * Звук приложения для показа экрана — Linux-оболочка, как у Discord.
 *
 * Звук берёт оболочка (`electron/sound.ts`): у выбранного приложения, по его
 * потокам воспроизведения, без голосов собеседников. Сюда приходят сырые
 * кадры, сводник в звуковом потоке (`sound-worklet.js`) складывает потоки, и
 * наружу выходит обычная `MediaStreamTrack` — её и публикует звонок.
 *
 * В вебе и в Windows этого пути нет: там звук показа даёт сам браузер
 * (вкладка) или нативный захват.
 */
import { fail, note } from "@shared/lib/journal";
import workletBody from "./sound-worklet.js?raw";

/** Приложение, у которого можно взять звук. */
export interface SoundApp {
  pid: number;
  name: string;
  binary: string;
  streams: number;
}

/** Идущий звук приложения: дорожка и её остановка. */
export interface AppSound {
  track: MediaStreamTrack;
  stop(): void;
}

let cached: Promise<boolean> | null = null;

/** Может ли оболочка дать звук приложения. Ответ не меняется — запоминается. */
export function hasAppSound(): Promise<boolean> {
  const sound = window.airelShell?.sound;
  if (!sound) return Promise.resolve(false);

  cached ??= sound.has().then(
    (has) => {
      note("звук", "оболочка отвечает про звук приложения", { есть: has });
      return has;
    },
    (error: unknown) => {
      fail("звук", "оболочка не ответила про звук приложения", error);
      return false;
    },
  );
  return cached;
}

/** Приложения, у которых звук есть прямо сейчас. */
export async function listSoundApps(): Promise<SoundApp[]> {
  try {
    return ((await window.airelShell?.sound?.apps()) ?? []) as SoundApp[];
  } catch (error) {
    fail("звук", "приложения со звуком не перечислились", error);
    return [];
  }
}

/**
 * Берёт звук процесса `pid` и отдаёт его дорожкой.
 *
 * Обработки голоса у дорожки нет: ни шумодава, ни АРУ — это звук игры или
 * фильма, а не речь, и сводится он сам, без `getUserMedia`. Два канала.
 */
export async function startAppSound(pid: number): Promise<AppSound> {
  const shell = window.airelShell?.sound;
  if (!shell) throw new Error("звук приложения есть только в оболочке под Linux");

  const context = new AudioContext({ sampleRate: 48_000, latencyHint: "interactive" });
  const unsubscribe: (() => void)[] = [];
  let track: MediaStreamTrack | undefined;

  const stop = () => {
    for (const off of unsubscribe) off();
    unsubscribe.length = 0;
    void shell.stop().catch(() => {});
    track?.stop();
    void context.close().catch(() => {});
  };

  try {
    await context.resume();
    const blob = URL.createObjectURL(new Blob([workletBody], { type: "text/javascript" }));
    try {
      await context.audioWorklet.addModule(blob);
    } finally {
      URL.revokeObjectURL(blob);
    }

    const node = new AudioWorkletNode(context, "app-sound", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    const destination = context.createMediaStreamDestination();
    destination.channelCount = 2;
    node.connect(destination);
    track = destination.stream.getAudioTracks()[0];

    unsubscribe.push(
      shell.onPcm((stream, bytes) => {
        // Копия ради ровного начала: смещение куска в общем буфере бывает
        // некратным четырём, и числа из него не собрать.
        const pcm = new Float32Array(bytes.slice().buffer);
        node.port.postMessage({ type: "pcm", stream, pcm }, [pcm.buffer]);
      }),
      shell.onGone((stream) => node.port.postMessage({ type: "gone", stream })),
    );

    await shell.start(pid);
    note("звук", "звук приложения пошёл", { процесс: pid });
    return { track, stop };
  } catch (error) {
    stop();
    throw error;
  }
}
